import { cn } from "@/lib/utils";
import type { ComparacaoDTO } from "@shared/mercado-cotacoes";

/**
 * Variação percentual como pill mono: `▲ 3,6%` (verde) · `▼ 3,4%` (vermelho) ·
 * `= 0,0%` (neutro). O sinal é SEMPRE a seta — nunca `+`/`−` soltos, que num
 * celular ao sol se perdem antes do número. `null` (sem base de comparação) vira
 * `—`, nunca `0,0%`: zero é uma informação, ausência é outra.
 */
export function Delta({ value, className, testId }: { value: number | null | undefined; className?: string; testId?: string }) {
  const base = "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[11px] font-semibold tabular-nums whitespace-nowrap";
  if (value === null || value === undefined || !isFinite(value)) {
    return <span className={cn(base, "bg-muted text-muted-foreground", className)} data-testid={testId}>—</span>;
  }
  const pct = Math.abs(value).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (value > 0.05) {
    return <span className={cn(base, "bg-sucesso-suave text-sucesso", className)} data-testid={testId}>▲ {pct}%</span>;
  }
  if (value < -0.05) {
    return <span className={cn(base, "bg-erro-suave text-erro", className)} data-testid={testId}>▼ {pct}%</span>;
  }
  return <span className={cn(base, "bg-muted text-muted-foreground", className)} data-testid={testId}>= {pct}%</span>;
}

/**
 * A comparação do produtor com a praça — na FORMA que o referencial permite.
 *
 * ⚠️ UM NÓ DE TEXTO SÓ, com `whitespace-nowrap`. Não é detalhe de estilo: com
 * o valor num span e o rótulo em outro, "−0,19 /kg" virava `R$` / `1,41` em
 * duas linhas e "para a placa" em três dentro da coluna estreita do quadro —
 * quebrado em produção no desktop a 1440. Número e rótulo que se leem juntos
 * quebram juntos ou não quebram; aqui não quebram.
 *
 * ⚠️ Com placa **ao produtor** as duas pontas medem a mesma coisa e sai o
 * percentual `vs praça`. Com placa **de venda** sai a diferença em R$/kg,
 * NEUTRA e rotulada `para a placa`: o número é a distância entre duas bases
 * diferentes (caixa embalada × pé de carga), não um desempenho. Por isso não
 * tem verde nem vermelho — pintar de vermelho seria escrever "vendeu abaixo da
 * praça", que é justamente a frase que a plataforma não diz.
 *
 * Texto tinto, sem pílula: numa linha densa "você: R$ 1,41 · −0,19 /kg para a
 * placa" a pílula do `Delta` empurraria o resto para fora. O `Delta` continua
 * sendo a pílula onde ele aparece sozinho (a variação na semana).
 */
export function Comparacao({ valor, className, testId }: { valor: ComparacaoDTO | null | undefined; className?: string; testId?: string }) {
  const base = "whitespace-nowrap font-mono text-[11px] font-semibold tabular-nums";
  if (!valor || !isFinite(valor.valor)) {
    return <span className={cn(base, "text-muted-foreground", className)} data-testid={testId}>—</span>;
  }

  if (valor.tipo === "PERCENTUAL") {
    const pct = Math.abs(valor.valor).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const tom = valor.valor > 0.05 ? "text-sucesso" : valor.valor < -0.05 ? "text-erro" : "text-muted-foreground";
    const seta = valor.valor > 0.05 ? "▲" : valor.valor < -0.05 ? "▼" : "=";
    return (
      <span className={cn(base, tom, className)} data-testid={testId}>{`${seta} ${pct}% vs praça`}</span>
    );
  }

  // O sinal vai explícito: aqui não há seta para carregá-lo, e "0,19 /kg" sem
  // sinal não diz para que lado.
  const sinal = valor.valor > 0.005 ? "+" : valor.valor < -0.005 ? "\u2212" : "";
  const abs = Math.abs(valor.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <span className={cn(base, "text-muted-foreground", className)} data-testid={testId}>
      {`${sinal}${abs} /kg para a placa`}
    </span>
  );
}
